import { Badge, Group, Stack, Text } from '@mantine/core';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from 'react-map-gl';

export interface ParcellesStackField {
  id: number;
  area: number | null;
  campaignYear: number;
  layerColor: string;
  telepacCode: string | null;
  identificationLabel: string;
  identificationSource: 'TELEPAC' | 'RPG';
  referenceCampaignYear: number | null;
}

export interface ParcellesFieldPopupData {
  longitude: number;
  latitude: number;
  fields: ParcellesStackField[];
}

interface ParcellesFieldPopupProps {
  data: ParcellesFieldPopupData | null;
  onClose: () => void;
}

export const ParcellesFieldPopup: FC<ParcellesFieldPopupProps> = ({
  data,
  onClose,
}) => {
  const { t } = useTranslation();
  if (!data) {
    return null;
  }

  return (
    <Popup
      anchor="bottom"
      closeOnClick={false}
      closeOnMove={false}
      latitude={data.latitude}
      longitude={data.longitude}
      maxWidth="320px"
      offset={[0, -10]}
      onClose={onClose}
    >
      <Stack gap="sm">
        {data.fields.length > 1 ? (
          <Text c="dimmed" fw={600} size="xs" tt="uppercase">
            {t('contracts.parcelles-stack-title', { count: data.fields.length })}
          </Text>
        ) : null}
        {data.fields.map((field) => (
          <Stack
            gap={4}
            key={`${field.id}-${field.campaignYear}`}
            style={{
              borderLeft: `3px solid ${field.layerColor}`,
              paddingLeft: 8,
            }}
          >
            <Group gap="xs">
              <Text fw={600} size="sm">
                {field.id}
              </Text>
              <Text c="dimmed" size="sm">
                {field.area != null
                  ? `${field.area.toFixed(2).replace('.', ',')} ha`
                  : '-'}
              </Text>
            </Group>
            <Group gap="xs">
              <Badge color="gray" size="sm" variant="light">
                {field.campaignYear}
              </Badge>
              <Text size="sm">{field.telepacCode ?? '-'}</Text>
              <Badge
                color={field.identificationSource === 'TELEPAC' ? 'blue' : 'grape'}
                size="sm"
                variant="light"
              >
                {field.identificationLabel}
              </Badge>
            </Group>
            {field.referenceCampaignYear != null ? (
              <Text c="dimmed" size="xs">
                {t('contracts.reference-campaign', {
                  year: field.referenceCampaignYear,
                })}
              </Text>
            ) : null}
          </Stack>
        ))}
      </Stack>
    </Popup>
  );
};
